"use client";

import { Link } from "@/navigation";
import { useLocale, useTranslations } from "next-intl";
import { AnimatedSection } from "@/components/ui/animated-section";
import {
  getProductsForCity,
  getLocalizedProductName,
  getLocalizedCityName,
} from "@/lib/product-location-data";

interface LocationProductLinksProps {
  /** City slug — a cross-city that has product landing pages (rotterdam | vlaardingen). */
  city: string;
}

/**
 * Internal-link grid from a primary city page to its
 * `/locations/[city]/[product]` occasion landing pages.
 *
 * Those city×product pages are generated and listed in the sitemap, but were
 * previously orphaned — no inbound links from any crawlable navigational entry
 * point — so they earned no internal link equity. Surfacing them here gives
 * Google a crawl path from the high-authority city page and concentrates
 * topical relevance on local occasion terms (e.g. "verjaardagstaart rotterdam",
 * "halal taart rotterdam"). Anchor text is the localized product name + city.
 */
export function LocationProductLinks({ city }: LocationProductLinksProps) {
  const locale = useLocale();
  const t = useTranslations("locations.productLinks");
  const products = getProductsForCity(city);

  if (products.length === 0) return null;

  const cityName = getLocalizedCityName(city, locale);
  const headingId = `location-products-heading-${city}`;

  return (
    <section
      className="py-12 md:py-16 bg-muted/50"
      aria-labelledby={headingId}
    >
      <div className="mx-auto max-w-[1536px] px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="fade-up" delay={0}>
          <h2
            id={headingId}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 text-left"
          >
            {t("title", { city: cityName })}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6 md:mb-8 max-w-3xl text-left">
            {t("description", { city: cityName })}
          </p>
        </AnimatedSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 md:gap-6">
          {products.map((product, index) => (
            <AnimatedSection
              key={product.productSlug}
              animation="fade-up"
              delay={index * 50}
            >
              <Link
                href={`/locations/${city}/${product.productSlug}` as never}
                className="block h-full p-5 rounded-xl border bg-background hover:border-primary/30 hover:shadow-md transition-all duration-200 text-left min-h-[44px]"
              >
                <h3 className="font-semibold text-base md:text-lg">
                  {getLocalizedProductName(product, locale)}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">{cityName}</p>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
